import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import { queriesPayerRows, queriesPayers } from "@query";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { selectorPayerDisplayName } from "../../../../shared/selectors/selectorPayerDisplayName";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";

type Value = string | null;

type Props = {
  value: Value;
  onChange: (value: Value) => void;
} & Omit<WSSelectSearchProps, "options" | "value" | "onChange">;

export const SelectPayer: React.FC<Props> = ({
  value,
  onChange,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const queryPayerRows = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    filter: {
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: value as string },
    { enabled: !!value, retry: false }
  );

  const payer = queryPayer.data;

  useEffect(() => {
    if (!payer) {
      if (value) {
        return;
      }

      setSelectValue(null);
      return;
    }

    const name = selectorPayerDisplayName(payer);

    setSelectValue({
      value: payer.id ?? "",
      label: name,
      labels: payer.email ? [payer.email] : [],
      avatar: {
        type: "text",
        text: name
      }
    });
  }, [payer, value]);

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayerRows.flatData ?? []).forEach(payer => {
      const name = selectorSearchRowName(payer);

      options.push({
        value: payer.id ?? "",
        label: name,
        labels: payer.email ? [payer.email] : [],
        avatar: {
          type: "text",
          text: name
        }
      });
    });

    return options;
  }, [queryPayerRows.flatData]);

  const onReachMenuEnd = useCallback(() => {
    if (queryPayerRows.hasNextPage) {
      queryPayerRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayerRows.hasNextPage]);

  return (
    <WSSelectSearch
      {...otherProps}
      value={selectValue}
      onChange={newOption => {
        if (newOption === null) {
          onChange(null);
          setSelectValue(null);
          return;
        } else if (newOption?.value) {
          onChange(newOption?.value as string);
          setSelectValue(newOption);
        }
      }}
      placeholder="All clients"
      options={options}
      status={queryPayerRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        (queryPayerRows.isFetching && !queryPayerRows.isFetchingNextPage) ||
        queryPayer.isInitialLoading
      }
      onReachMenuEnd={onReachMenuEnd}
    />
  );
};
